/**
 * IndexedDB copy of the procedure codebook, so the app shows codes immediately on start (and
 * offline on native) while `AppDataService` checks the server version in the background.
 * `tasks/procedure-codebook.md` Phase 2.
 *
 * ── NO CACHED HANDLE ─────────────────────────────────────────────────────────────────
 *
 * `tasks/lessons.md` §16: browsers force-close IndexedDB connections, and a cached handle stays
 * truthy while every transaction on it throws. This is read once per session and written only when
 * the server version moves, so every operation opens its own connection and closes it, and a
 * failed operation is retried once on a fresh one. Nothing here can hand out a dead handle.
 *
 * Failure is never fatal: a read that fails returns null (the app fetches from the server), and a
 * write that fails only costs the next cold start a download.
 */

export interface CachedCodebook {
  version: number;
  codes: any[];
  savedAt: number;
}

const DB_NAME = 'medicai-procedure-codebook';
const DB_VERSION = 1;
const STORE = 'codebook';
const KEY = 'current';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const attempt = async (): Promise<T> => {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = run(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(request.result);
        tx.onerror = () => reject(tx.error || request.error);
        tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
      });
    } finally {
      db.close();
    }
  };
  try {
    return await attempt();
  } catch {
    return attempt(); // once, on a fresh connection
  }
}

export async function readCachedCodebook(): Promise<CachedCodebook | null> {
  try {
    const value = await withStore<CachedCodebook | undefined>('readonly', (store) => store.get(KEY));
    return value && Array.isArray(value.codes) && typeof value.version === 'number' ? value : null;
  } catch (error) {
    console.warn('Procedure codebook cache unreadable; will fetch from the server', error);
    return null;
  }
}

export async function writeCachedCodebook(version: number, codes: any[]): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.put({ version, codes, savedAt: Date.now() } as CachedCodebook, KEY));
  } catch (error) {
    console.warn('Procedure codebook cache not saved', error);
  }
}
